"use client";
import { PropsWithChildren, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { VisibleItemsManager } from "./VisibleItemsManager";

interface VirtualGalleryItemProps extends PropsWithChildren {
    /** 项目高度 */
    itemHeight: number;
    /** 项目宽度 */
    itemWidth: number;
    /** 预加载项目数量 */
    preloadItemCount: number;
    /** 数据索引 */
    dataIndex: number;
    /** 可视项目管理器 */
    visibleItemsManager: VisibleItemsManager;
    /** 项目样式 */
    style?: React.CSSProperties;
    /** 类名 */
    className?: string;
}

/**
 * 虚拟Gallery项目组件，参考CustomRow实现
 * 使用IntersectionObserver来检测可见性，实现虚拟化
 */
export const VirtualGalleryItem = (props: VirtualGalleryItemProps) => {
    const {
        children,
        itemHeight,
        itemWidth,
        preloadItemCount,
        dataIndex,
        visibleItemsManager,
        style,
        className,
        ...restProps
    } = props;

    const itemRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const observerRef = useRef<IntersectionObserver | null>(null);

    // 创建 observer 的函数，便于窗口调整时重新创建
    const createObserver = useCallback(() => {
        // 检查环境是否支持 IntersectionObserver
        if (typeof IntersectionObserver === 'undefined') {
            setIsVisible(true);
            if (visibleItemsManager && typeof dataIndex === 'number') {
                visibleItemsManager.updateIndices(dataIndex, true);
            }
            return null;
        }

        try {
            // 计算需要预加载的项目高度 - 为快速滚动优化增加更大的缓冲区
            const basePreloadMargin = itemHeight * preloadItemCount;
            // 增加额外的缓冲区以应对快速滚动
            const fastScrollBuffer = itemHeight * Math.max(5, Math.floor(preloadItemCount * 0.3));
            const totalPreloadMargin = basePreloadMargin + fastScrollBuffer;
            const preloadMargin = `${totalPreloadMargin}px`;

            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        const newIsVisible = entry.isIntersecting;
                        setIsVisible(newIsVisible);

                        if (visibleItemsManager && typeof dataIndex === 'number') {
                            visibleItemsManager.updateIndices(dataIndex, newIsVisible);
                        }
                    });
                },
                {
                    threshold: 0.01, // 降低阈值，更早触发可见性检测
                    // 设置更大的 rootMargin 以应对快速滚动
                    rootMargin: `${preloadMargin} 0px ${preloadMargin} 0px`,
                },
            );

            return observer;
        } catch (error) {
            // 出错时默认显示所有项目
            setIsVisible(true);
            if (visibleItemsManager && typeof dataIndex === 'number') {
                visibleItemsManager.updateIndices(dataIndex, true);
            }
            return null;
        }
    }, [itemHeight, preloadItemCount, dataIndex, visibleItemsManager]);

    // 初始化和清理 observer
    useEffect(() => {
        // 服务器端渲染时不执行
        if (typeof window === 'undefined') { return; }

        const currentRef = itemRef.current;
        if (!currentRef) { return; }

        // 创建 observer
        observerRef.current = createObserver();

        // 如果成功创建了 observer，开始观察
        if (observerRef.current) {
            observerRef.current.observe(currentRef);
        }

        return () => {
            // 清理 observer
            if (observerRef.current && currentRef) {
                observerRef.current.unobserve(currentRef);
                observerRef.current.disconnect();
            }

            // 从全局状态中移除此项目
            if (visibleItemsManager && typeof dataIndex === 'number') {
                visibleItemsManager.updateIndices(dataIndex, false);
            }
        };
    }, [createObserver, dataIndex, visibleItemsManager]);

    // 处理窗口大小调整
    useEffect(() => {
        // 服务器端渲染时不执行
        if (typeof window === 'undefined') { return; }

        const handleResize = () => {
            const currentRef = itemRef.current;
            if (!currentRef) { return; }

            // 清理旧的 observer
            if (observerRef.current) {
                observerRef.current.unobserve(currentRef);
                observerRef.current.disconnect();
            }

            // 创建新的 observer
            observerRef.current = createObserver();

            // 如果成功创建了 observer，开始观察
            if (observerRef.current) {
                observerRef.current.observe(currentRef);
            }
        };

        // 添加窗口大小调整事件监听
        window.addEventListener('resize', handleResize);

        return () => {
            // 移除事件监听
            window.removeEventListener('resize', handleResize);
        };
    }, [createObserver]);

    // 使用 useMemo 缓存可见性检查
    const shouldRender = useMemo(() => {
        if (!visibleItemsManager || typeof dataIndex !== 'number') {
            return true;
        }

        // 对于前几个项目，总是渲染（确保初始内容显示）
        const initialRenderCount = Math.min(preloadItemCount * 2, 20);
        if (dataIndex < initialRenderCount) {
            return true;
        }

        // 如果项目当前可见，总是渲染
        if (isVisible) {
            return true;
        }

        // 使用增强的预加载逻辑判断
        return visibleItemsManager.shouldBeVisible(dataIndex, preloadItemCount);
    }, [visibleItemsManager, dataIndex, preloadItemCount, isVisible]);

    return (
        <div
            {...restProps}
            ref={itemRef}
            className={className}
            data-visible={isVisible}
            data-item-index={isVisible ? dataIndex : null}
            style={{
                width: itemWidth,
                height: itemHeight,
                ...style,
            }}
        >
            {shouldRender ? children : null}
        </div>
    );
};
